// AI-CONTEXT: See docs/features/datakwaliteitsscore.md
import { useParams, Redirect } from 'react-router-dom'
import useDataQualityScore from '../../hooks/useDataQualityScore'
import useDataQualityDetails from '../../hooks/useDataQualityDetails'
import {
    dataQualityBand,
    DIMENSION_LABELS,
    DIMENSION_EXPLANATIONS,
    INDICATOR_LABELS,
    INDICATOR_EXPLANATIONS,
    ERROR_TYPE_LABELS,
    indicatorCountLine,
    formatMonthLabel,
    retiredAnswersNote,
} from '../../helpers/dashboard/dataQualityDisplay'
import PageHeader from '../../components/layout/PageHeader'
import { client } from '../../hooks/Client'

const INDICATORS_PER_DIMENSION = {
    dekking: ['D1', 'D2', 'D3', 'D4'],
    volledigheid: ['V1', 'V2', 'V3', 'V4'],
    integriteit: [],
    actualiteit: ['A1', 'A2', 'A3'],
}

/**
 * Blok met een gecapte lijst: kop, aantal en items; toont een melding wanneer
 * de lijst is afgekapt op de eerste 100.
 *
 * @param {object} props
 * @param {string} props.title - Kop boven de lijst
 * @param {{total: number, items: Array<object>}} props.list - Gecapte lijst uit de details-callable
 * @param {(item: object, index: number) => React.ReactNode} props.renderItem - Rij-renderer
 * @param {string} props.emptyText - Tekst wanneer er niets te tonen is (dat is goed nieuws)
 */
const DetailList = ({ title, list, renderItem, emptyText }) => (
    <div className='data-quality-detail-list'>
        <h4>{title} ({list.total})</h4>
        {list.total === 0 ? (
            <p className='data-quality-detail-empty'>{emptyText}</p>
        ) : (
            <>
                <ul>
                    {list.items.map((item, index) => (
                        <li key={item.ID ?? item.timestampMs ?? (typeof item === 'string' ? item : index)}>
                            {renderItem(item, index)}
                        </li>
                    ))}
                </ul>
                {list.total > list.items.length && (
                    <p className='data-quality-detail-cap'>
                        Eerste {list.items.length} van {list.total} getoond.
                    </p>
                )}
            </>
        )}
    </div>
)

const formatDate = (msValue) => new Date(msValue).toLocaleDateString('nl-NL')

/**
 * Ruwe-data-blokken per dimensie. Deelnemer-lijsten zijn leeg bij een anoniem
 * meetprofiel; een lege lijst betekent verder gewoon: niets te herstellen.
 *
 * @param {object} props
 * @param {string} props.dimension - dekking | volledigheid | integriteit | actualiteit
 * @param {object} props.details - Payload van getDataQualityDetails
 */
const DimensionRawData = ({ dimension, details }) => {
    if (dimension === 'dekking') {
        const { unmeasuredParticipants, participantsMissingMoments, monthsWithoutRegistration, unmeasuredGroups } = details.dekking
        return (
            <>
                <DetailList
                    title='Relevante deelnemers zonder meting (D1)'
                    list={unmeasuredParticipants}
                    renderItem={(p) => p.Name || p.ID}
                    emptyText='Alle relevante deelnemers zijn gemeten.'
                />
                <DetailList
                    title='Gemeten deelnemers met ontbrekende meetmomenten (D2)'
                    list={participantsMissingMoments}
                    renderItem={(p) => `${p.Name || p.ID} — mist: ${p.missingMoments.join(', ')}`}
                    emptyText='Alle gemeten deelnemers hebben hun vereiste meetmomenten.'
                />
                <DetailList
                    title='Actieve maanden zonder registratie (D3)'
                    list={monthsWithoutRegistration}
                    renderItem={(month) => formatMonthLabel(month)}
                    emptyText='Elke actieve maand heeft minimaal één registratie.'
                />
                <DetailList
                    title='Trajecten of doelgroepen zonder meting (D4)'
                    list={unmeasuredGroups}
                    renderItem={(g) => g.Title || g.ID}
                    emptyText='Alle actieve trajecten of doelgroepen zijn gemeten.'
                />
            </>
        )
    }

    if (dimension === 'volledigheid') {
        const { incompleteSubmissions, participantsMissingPost } = details.volledigheid
        return (
            <>
                <DetailList
                    title='Onvolledige inzendingen (V1/V2)'
                    list={incompleteSubmissions}
                    renderItem={(s) => (
                        `${formatDate(s.timestampMs)} — ${s.questionnaireTitle || 'onbekende vragenlijst'}` +
                        `${s.personaName ? ` — ${s.personaName}` : ''} — ${s.answered} van ${s.expected} vragen beantwoord`
                    )}
                    emptyText='Alle inzendingen zijn volledig ingevuld.'
                />
                <DetailList
                    title='Uitgestroomde deelnemers zonder gekoppelde nameting (V3)'
                    list={participantsMissingPost}
                    renderItem={(p) => p.Name || p.ID}
                    emptyText='Alle uitgestroomde, gemeten deelnemers hebben een gekoppelde voor- én nameting.'
                />
            </>
        )
    }

    if (dimension === 'integriteit') {
        const { errorTypeCounts, faultyRecords, retiredRecords } = details.integriteit
        const typeEntries = Object.entries(errorTypeCounts)
        const retiredNote = retiredAnswersNote(retiredRecords)
        return (
            <>
                {retiredNote && <p className='data-quality-detail-note'>{retiredNote}</p>}
                <div className='data-quality-detail-list'>
                    <h4>Fouten per type</h4>
                    {typeEntries.length === 0 ? (
                        <p className='data-quality-detail-empty'>Geen foute records gevonden.</p>
                    ) : (
                        <ul>
                            {typeEntries.map(([type, count]) => (
                                <li key={type}>{ERROR_TYPE_LABELS[type] || type}: <strong>{count}</strong></li>
                            ))}
                        </ul>
                    )}
                </div>
                <DetailList
                    title='Foute records'
                    list={faultyRecords}
                    renderItem={(r) => (
                        `${formatDate(r.timestampMs)} — ${r.fieldQuestion || 'onbekende vraag'}` +
                        `${r.personaName ? ` — ${r.personaName}` : ''} — ` +
                        r.errors.map((type) => ERROR_TYPE_LABELS[type] || type).join(', ')
                    )}
                    emptyText='Geen foute records gevonden.'
                />
            </>
        )
    }

    const { lastRegistrationMs, registrationsPerMonth, longestGapMonths } = details.actualiteit
    return (
        <>
            <div className='data-quality-detail-list'>
                <h4>Laatste registratie</h4>
                <p>{lastRegistrationMs ? formatDate(lastRegistrationMs) : 'Nog geen registraties'}</p>
                {longestGapMonths !== null && (
                    <p>Langste onderbreking: {Math.round(longestGapMonths)} {Math.round(longestGapMonths) === 1 ? 'maand' : 'maanden'}</p>
                )}
            </div>
            <div className='data-quality-detail-list'>
                <h4>Inzendingen per maand</h4>
                {registrationsPerMonth.length === 0 ? (
                    <p className='data-quality-detail-empty'>Nog geen inzendingen in de analyseperiode.</p>
                ) : (
                    <ul>
                        {registrationsPerMonth.map((entry) => (
                            <li key={entry.month}>{formatMonthLabel(entry.month)}: <strong>{entry.submissions}</strong></li>
                        ))}
                    </ul>
                )}
            </div>
        </>
    )
}

/**
 * Onderzoekspagina per datakwaliteit-dimensie: de indicatorscores mét de
 * onderliggende ruwe data, zodat een organisatie precies ziet wáár het gat
 * zit. Route: /:client/datakwaliteit/:dimension.
 */
const DataQualityDimensionDetail = () => {
    const { dimension } = useParams()
    const { stored } = useDataQualityScore()
    const { details, loading, error } = useDataQualityDetails()

    if (!DIMENSION_LABELS[dimension]) {
        return <Redirect to={`/${client}/datakwaliteit`} />
    }

    const dimensionDetails = stored?.Breakdown?.[dimension]

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader
                    heading={`Datakwaliteit — ${DIMENSION_LABELS[dimension]}`}
                    description={DIMENSION_EXPLANATIONS[dimension]}
                />

                {dimensionDetails && (
                    <div className='data-quality-dimension data-quality-dimension--static'>
                        <div className='data-quality-dimension-header'>
                            <h3>Indicatoren</h3>
                            <span className={`data-quality-dimension-score data-quality-score--${dataQualityBand(Math.round(dimensionDetails.score))}`}>
                                {Math.round(dimensionDetails.score)}
                            </span>
                        </div>
                        {dimension === 'integriteit' ? (
                            <>
                                <p className='data-quality-indicator'>
                                    Foutvrije records: {dimensionDetails.totalRecords - dimensionDetails.faultyRecords} van {dimensionDetails.totalRecords}
                                </p>
                                {retiredAnswersNote(dimensionDetails.retiredRecords) && (
                                    <p className='data-quality-modal-note'>
                                        {retiredAnswersNote(dimensionDetails.retiredRecords)}
                                    </p>
                                )}
                            </>
                        ) : (
                            INDICATORS_PER_DIMENSION[dimension].map((code) => {
                                const value = dimensionDetails[code]
                                const countLine = indicatorCountLine(code, dimensionDetails.counts)
                                return (
                                    <div key={code} className='data-quality-modal-indicator'>
                                        <p className='data-quality-indicator'>
                                            <span className='data-quality-indicator-code'>{code}</span>
                                            {INDICATOR_LABELS[code]}:{' '}
                                            {value === null || value === undefined
                                                ? 'niet berekenbaar — gewicht herverdeeld'
                                                : `${Math.round(value)} op 100`}
                                        </p>
                                        <p className='data-quality-modal-note'>{INDICATOR_EXPLANATIONS[code]}</p>
                                        {countLine && <p className='data-quality-modal-count'>{countLine}</p>}
                                    </div>
                                )
                            })
                        )}
                    </div>
                )}

                <h3 className='data-quality-rawdata-heading'>Onderliggende data</h3>
                {error ? (
                    <p className='data-quality-empty'>
                        De onderliggende data kon niet worden geladen. Vernieuw de pagina of probeer het later opnieuw.
                    </p>
                ) : loading || !details ? (
                    <p className='data-quality-empty'>Onderliggende data wordt berekend…</p>
                ) : (
                    <DimensionRawData dimension={dimension} details={details} />
                )}
            </div>
        </div>
    )
}

export default DataQualityDimensionDetail
